import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { maskDocument, maskPhone, maskCep } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Users, Search, ChevronRight, Pencil, X, Save } from "lucide-react";
import { brl, fdate } from "@/pages/portal/Dashboard";

const inputCls =
  "mt-1.5 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600";
const labelCls = "text-xs uppercase tracking-wider text-slate-400";

const FILTERS = [
  ["", "Todos"],
  ["active", "Clientes ativos"],
  ["open", "Com orçamento em aberto"],
  ["approved", "Com orçamento aprovado"],
  ["inactive", "Sem orçamento recente"],
];

export default function Customers() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");

  const load = async (query = q, f = filter) => {
    try {
      const { data } = await api.get("/customers", { params: { q: query, filter: f } });
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { load("", ""); }, []);

  const changeFilter = (f) => { setFilter(f); load(q, f); };
  const search = (e) => { e.preventDefault(); load(q, filter); };

  const [edit, setEdit] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const PAYMENT_METHODS_ADMIN = [
    { key: "dinheiro", label: "Dinheiro" },
    { key: "pix", label: "Pix" },
    { key: "cartao_debito", label: "Cartão Débito (+2%)" },
    ...Array.from({ length: 12 }, (_, i) => ({ key: `credito_${i + 1}x`, label: `Crédito ${i + 1}x (+${i + 3}%)` })),
    { key: "boleto_5", label: "Boleto 5 dias" },
    { key: "boleto_15", label: "Boleto 15 dias" },
    { key: "boleto_30", label: "Boleto 30 dias" },
    { key: "boleto_15_30", label: "Boleto 15 / 30 dias" },
    { key: "boleto_30_60", label: "Boleto 30 / 60 dias" },
    { key: "boleto_15_30_45", label: "Boleto 15 / 30 / 45 dias" },
    { key: "boleto_30_60_90", label: "Boleto 30 / 60 / 90 dias" },
  ];
  const DEFAULT_PM_KEYS = ["dinheiro", "pix", "cartao_debito", ...Array.from({ length: 12 }, (_, i) => `credito_${i + 1}x`)];

  const openEdit = async (c) => {
    try {
      const { data } = await api.get(`/customers/${c.id}`);
      const k = data.customer || {};
      setEdit({
        id: c.id,
        name: k.name || "", trade_name: k.trade_name || "",
        document: k.document_fmt || k.document || "",
        email: k.email || "", phone: k.phone || "", whatsapp: k.whatsapp || "",
        cep: k.cep || "", address: k.address || "", address_number: k.address_number || "",
        complement: k.complement || "", neighborhood: k.neighborhood || "",
        city: k.city || "", state: k.state || "", notes: k.notes || "",
        payment_condition: k.payment_condition || "Dinheiro, Pix / Transferência Bancária",
        payment_methods: k.payment_methods?.length ? k.payment_methods : DEFAULT_PM_KEYS,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!edit.name.trim()) {
      toast.error("Informe o nome/razão social.");
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/customers/${edit.id}`, {
        name: edit.name, trade_name: edit.trade_name, document: edit.document,
        email: edit.email, phone: edit.phone, whatsapp: edit.whatsapp,
        cep: edit.cep, address: edit.address, address_number: edit.address_number,
        complement: edit.complement, neighborhood: edit.neighborhood,
        city: edit.city, state: edit.state, notes: edit.notes,
        payment_condition: edit.payment_condition,
        payment_methods: edit.payment_methods,
      });
      toast.success("Cliente atualizado — cadastro existente, sem duplicidade. Orçamentos antigos preservados.");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div data-testid="customers-page">
      <h1 className="display text-3xl font-extrabold uppercase">Clientes</h1>
      <p className="mt-1 text-sm text-slate-500">
        Cadastro permanente por CPF/CNPJ — criado automaticamente a cada solicitação de orçamento.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <form onSubmit={search} className="flex items-center gap-2 flex-1 min-w-[260px]">
          <input
            data-testid="customers-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por CPF/CNPJ, nome, e-mail, telefone ou protocolo…"
            className="flex-1 bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600"
          />
          <button data-testid="customers-search-button" className="pill-accent rounded-md px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">
            <Search className="w-4 h-4" />
          </button>
        </form>
        <div className="flex flex-wrap gap-2" data-testid="customers-filters">
          {FILTERS.map(([v, l]) => (
            <button
              key={v}
              data-testid={`customers-filter-${v || "all"}`}
              onClick={() => changeFilter(v)}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-md border transition-colors ${
                filter === v ? "bg-orange-600 border-orange-600 text-white" : "border-slate-700 text-slate-400 hover:border-orange-500"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 card-tactical rounded-md overflow-hidden">
        {!items ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>
        ) : !items.length ? (
          <div className="py-16 text-center" data-testid="customers-empty">
            <Users className="w-10 h-10 text-slate-700 mx-auto" strokeWidth={1.25} />
            <p className="mt-3 text-sm text-slate-500">Nenhum cliente encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="px-5 py-3.5 font-medium">Cliente</th>
                  <th className="px-5 py-3.5 font-medium">CPF/CNPJ</th>
                  <th className="px-5 py-3.5 font-medium">Desde</th>
                  <th className="px-5 py-3.5 font-medium">Última solicitação</th>
                  <th className="px-5 py-3.5 font-medium text-right">Orçamentos</th>
                  <th className="px-5 py-3.5 font-medium text-right">Em aberto</th>
                  <th className="px-5 py-3.5 font-medium text-right">Valor total</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {items.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-900/50 transition-colors" data-testid={`customer-row-${c.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="text-slate-200 font-medium">{c.name}</div>
                      <div className="text-xs text-slate-500">{[c.city, c.state].filter(Boolean).join("/") || c.email}</div>
                    </td>
                    <td className="px-5 py-3.5 mono text-slate-400">{c.document_fmt}</td>
                    <td className="px-5 py-3.5 text-slate-400">{fdate(c.created_at)}</td>
                    <td className="px-5 py-3.5">
                      <div className="text-slate-400">{fdate(c.last_request_at)}</div>
                      {c.last_quote_number && <div className="mono text-[11px] text-slate-600">{c.last_quote_number}</div>}
                    </td>
                    <td className="px-5 py-3.5 mono text-right text-slate-300">{c.quotes_count}</td>
                    <td className="px-5 py-3.5 mono text-right text-slate-300">{c.open_quotes}</td>
                    <td className="px-5 py-3.5 mono text-right text-slate-200">{c.total_value ? brl(c.total_value) : "—"}</td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="inline-flex items-center gap-4">
                        <button
                          onClick={() => openEdit(c)}
                          data-testid={`customer-edit-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-400 transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Editar
                        </button>
                        <Link
                          to={`/clientes-admin/${c.id}`}
                          data-testid={`customer-open-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-orange-500 hover:text-orange-400"
                        >
                          Abrir <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setEdit(null)}>
          <form onSubmit={saveEdit} className="card-tactical rounded-md p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="customer-edit-form">
            <div className="flex items-center justify-between">
              <h3 className="display text-base font-bold uppercase tracking-wide">Editar cliente</h3>
              <button type="button" onClick={() => setEdit(null)} data-testid="customer-edit-close">
                <X className="w-4 h-4 text-slate-500 hover:text-slate-300" />
              </button>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nome / Razão Social *</label>
                <input data-testid="customer-edit-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Nome Fantasia</label>
                <input data-testid="customer-edit-trade-name" value={edit.trade_name} onChange={(e) => setEdit({ ...edit, trade_name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>CPF / CNPJ</label>
                <input data-testid="customer-edit-document" value={edit.document} onChange={(e) => setEdit({ ...edit, document: maskDocument(e.target.value) })} className={`${inputCls} mono`} placeholder="000.000.000-00" />
              </div>
              <div>
                <label className={labelCls}>E-mail</label>
                <input type="email" data-testid="customer-edit-email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Telefone</label>
                <input data-testid="customer-edit-phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: maskPhone(e.target.value) })} className={inputCls} placeholder="(00) 0000-0000" />
              </div>
              <div>
                <label className={labelCls}>WhatsApp</label>
                <input data-testid="customer-edit-whatsapp" value={edit.whatsapp} onChange={(e) => setEdit({ ...edit, whatsapp: maskPhone(e.target.value) })} className={inputCls} placeholder="(00) 00000-0000" />
              </div>
              <div>
                <label className={labelCls}>CEP</label>
                <input data-testid="customer-edit-cep" value={edit.cep} onChange={(e) => setEdit({ ...edit, cep: maskCep(e.target.value) })} className={`${inputCls} mono`} placeholder="00000-000" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Endereço</label>
                <input data-testid="customer-edit-address" value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Número</label>
                <input data-testid="customer-edit-number" value={edit.address_number} onChange={(e) => setEdit({ ...edit, address_number: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Complemento</label>
                <input data-testid="customer-edit-complement" value={edit.complement} onChange={(e) => setEdit({ ...edit, complement: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Bairro</label>
                <input data-testid="customer-edit-neighborhood" value={edit.neighborhood} onChange={(e) => setEdit({ ...edit, neighborhood: e.target.value })} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Formas de pagamento autorizadas</label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="customer-edit-payment-methods">
                  {PAYMENT_METHODS_ADMIN.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                      <input type="checkbox" data-testid={`customer-edit-pm-${m.key}`}
                        checked={(edit.payment_methods || []).includes(m.key)}
                        onChange={() => setEdit((e) => {
                          const cur = e.payment_methods || [];
                          return { ...e, payment_methods: cur.includes(m.key) ? cur.filter((x) => x !== m.key) : [...cur, m.key] };
                        })}
                        className="accent-orange-500 w-4 h-4" />
                      {m.label}
                    </label>
                  ))}
                </div>
                <p className="mt-1.5 text-[10px] text-zinc-600">
                  Somente as formas ativadas aparecem no orçamento deste cliente. Os acréscimos de cartão são internos e nunca são exibidos ao cliente.
                </p>
              </div>
              <div>
                <label className={labelCls}>Cidade</label>
                <input data-testid="customer-edit-city" value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Estado (UF)</label>
                <input data-testid="customer-edit-state" maxLength={2} value={edit.state} onChange={(e) => setEdit({ ...edit, state: e.target.value.toUpperCase() })} className={inputCls} placeholder="SP" />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={labelCls}>Observações</label>
                <textarea rows={2} data-testid="customer-edit-notes" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className={`${inputCls} resize-none`} />
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-500">
              A edição atualiza o cadastro existente (nunca cria duplicidade) e não altera orçamentos/histórico já registrados.
            </p>
            <div className="mt-5 flex gap-3">
              <button data-testid="customer-edit-save" disabled={editSaving}
                className="inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
                {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar alterações
              </button>
              <button type="button" onClick={() => setEdit(null)}
                className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-300">Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
